import { FormattedLearnerSubscriptionPlan, ReferralCode } from "../../../types";

type PartialPlan = Pick<
  FormattedLearnerSubscriptionPlan,
  "interval" | "learnerPrice" | "additionalLearnerPrice"
>;

/** Rounds a number to at most two decimals accurately (hopefully) */
function roundToTwoDecimals(num: number): number {
  // JS numbers are weird. I tried these other methods but they round 4.975 to
  // 4.97 which is ironically what Stripe does (but probably intentionally).
  //   Math.round(num * 100) / 100
  //   Number(num.toFixed(2))
  return Number(Math.round(Number(num + "e+2")) + "e-2");
}

/**
 * Calculates percent off to match Stripe. Note: this intentionally rounds
 * incorrectly to match Stripe.
 */
function calculatePercentOff(price: number, percentOff: number): number {
  const newPrice = Math.round(price * 100) * (1 - percentOff / 100);
  // It seems Stripe rounds in favor of the customer if a discount yields a
  // fraction of a cent. So we do the same here. For example, 50% off of 9.95
  // is 4.975, so return 4.97 instead of 4.98.
  return Math.floor(newPrice) / 100;
}

/** The full price if there's a discount */
export function getStrikethroughPriceAmount(
  plan: PartialPlan,
  referral: ReferralCode | undefined,
  quantity: number
): number {
  if (quantity < 1) {
    throw new Error("Quantity must be at least 1");
  }
  if (referral) {
    return (
      roundToTwoDecimals(
        (plan.learnerPrice.value +
          plan.additionalLearnerPrice.value * (quantity - 1)) *
          100
      ) / 100
    );
  }
  // Increase the normal price to make it appear discounted
  return plan.interval === "monthly"
    ? 13.95 + (quantity - 1) * 6.95
    : 135 + (quantity - 1) * 65;
}

/** The price we intend to charge */
export function getActualPriceAmount(
  plan: PartialPlan,
  referral: Pick<ReferralCode, "monthlyCoupon" | "annualCoupon"> | undefined,
  quantity: number
): number {
  if (quantity < 1) {
    throw new Error("Quantity must be at least 1");
  }
  const fullPrice =
    plan.learnerPrice.value +
    plan.additionalLearnerPrice.value * (quantity - 1);
  if (referral) {
    return roundToTwoDecimals(
      calculatePercentOff(
        fullPrice,
        plan.interval === "monthly"
          ? referral.monthlyCoupon.percent_off
          : referral.annualCoupon.percent_off
      )
    );
  }
  return roundToTwoDecimals(fullPrice);
}
